import React, { useEffect, useState, useMemo } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist-min';
const Plot = createPlotlyComponent(Plotly);
import {
    SectorWeightDetail,
    SectorWeightResponse,
    SectorWeightAlerts,
    SectorCompanyDetail,
    BenchmarkCompanyDetail,
} from '@/types/client';
import { sectorWeightAPI } from '@/services/api';
import { formatDate } from '@/utils/formatters';
import styles from './SectorWeightComparison.module.css';
import shared from '@/components/utils/shared.module.css';
import { Surface, Note, Button, Icon, Placeholder, StatGrid, TabBar, signedClass } from '@/components/ui';
import { chartInk, chartSequential, plotlyLayout, sectorColor } from '@/theme/chartColors';

interface SectorWeightComparisonProps {
    requestParams: { scheme_codes?: string; benchmark?: string; investment_strategy_codes?: string; date?: string };
    benchmarkOptions: { value: string; label: string }[];
    refreshKey: number;
    loadingFilters: boolean;
}

/** Sentinel value for the portfolio tab — used in modalTab state comparisons. */
const PORTFOLIO_TAB = 'portfolio';

const formatNumber = (value: number, decimals = 2, divisor = 1): string => {
    const val = value / divisor;
    return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(val);
};

const formatAUM = (value: number): string => {
    if (value >= 10000000) {
        return `₹${formatNumber(value, 2, 10000000)} Cr`;
    } else if (value >= 100000) {
        return `₹${formatNumber(value, 2, 100000)} L`;
    }
    return `₹${formatNumber(value, 2)}`;
};

/* Sectors take one distinct colour each (see `sectorColor`); the grouped
   "Others" slice is recessive, not a category hue. */
const CHART_COLORS = {
    others: chartInk.axis,
};

/** The heatmap's magnitude scale: the sequential ramp spread evenly over 0–1. */
const HEATMAP_SCALE: [number, string][] = chartSequential.map(
    (colour, idx) => [idx / (chartSequential.length - 1), colour],
);

/** The warning triangle beside an unclassified sector; its colour comes from the badge. */
const WarningGlyph: React.FC<{ size: number }> = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
);

interface SectorDetailModalProps {
    selectedSector: SectorWeightDetail;
    modalTab: string;
    setModalTab: (tab: string) => void;
    onClose: () => void;
    benchmarkOptions: { value: string; label: string }[];
}

const SectorDetailModal: React.FC<SectorDetailModalProps> = ({
    selectedSector,
    modalTab,
    setModalTab,
    onClose,
    benchmarkOptions,
}) => {
    const getBenchmarkName = (symbol: string) =>
        benchmarkOptions.find(opt => opt.value === symbol)?.label || symbol;

    const benchmarkTabs = Object.entries(selectedSector.benchmark_companies || {}).filter(
        ([, companies]) => companies.length > 0
    );

    const activeBmCompanies = modalTab !== PORTFOLIO_TAB
        ? (selectedSector.benchmark_companies?.[modalTab] || []) : [];

    // activeBmWeight is looked up by benchmark_name, which must equal the modalTab key from
    // benchmark_companies. Both are the benchmark's index symbol — kept in sync by the backend.
    const activeBmWeight = modalTab !== PORTFOLIO_TAB
        ? (selectedSector.benchmarks.find(b => b.benchmark_name === modalTab)?.weight || 0) : 0;

    const renderPortfolioRows = (companies: SectorCompanyDetail[]) =>
        companies.map((company, idx) => (
            <tr key={company.symbol}>
                <td>{idx + 1}</td>
                <td className={styles['company-name-text']}>{company.name}</td>
                <td className={styles['company-symbol-text']}>{company.symbol}</td>
                <td className={styles['td-number']}>
                    <div className={styles['weight-badge']}>
                        {company.weight < 0.01 && company.weight > 0
                            ? '< 0.01%'
                            : `${formatNumber(company.weight)}%`}
                    </div>
                </td>
                <td className={styles['td-number']}>
                    <span className={styles['aum-text']}>{formatAUM(company.aum)}</span>
                </td>
            </tr>
        ));

    const renderBenchmarkRows = (companies: BenchmarkCompanyDetail[]) =>
        companies.map((company, idx) => (
            <tr key={company.symbol}>
                <td>{idx + 1}</td>
                <td className={styles['company-name-text']}>{company.name}</td>
                <td className={styles['company-symbol-text']}>{company.symbol}</td>
                <td className={styles['td-number']}>
                    <div className={styles['weight-badge']}>
                        {company.weight < 0.01 && company.weight > 0
                            ? '< 0.01%'
                            : `${formatNumber(company.weight)}%`}
                    </div>
                </td>
                <td className={styles['td-number']}>
                    <span className={styles['aum-text']}>
                        {company.free_float_market_cap_cr != null
                            ? `₹${formatNumber(company.free_float_market_cap_cr)}`
                            : '—'}
                    </span>
                </td>
            </tr>
        ));

    return (
        <div className={styles['modal-overlay']} onClick={onClose}>
            <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
                <div className={styles['modal-header']}>
                    <div style={{ flex: 1 }}>
                        <h3 className={styles['modal-title']}>
                            {selectedSector.sector_name}
                            {selectedSector.is_unclassified && (
                                <span className={styles['unclassified-badge']}>
                                    <WarningGlyph size={12} />
                                    No Sector
                                </span>
                            )}
                        </h3>
                        <div className={styles['modal-header-stats']}>
                            {modalTab === PORTFOLIO_TAB ? (
                                <>
                                    <div className={styles['modal-header-stat']}>
                                        Portfolio Weight: <strong>{formatNumber(selectedSector.portfolio_weight)}%</strong>
                                    </div>
                                    <div className={styles['modal-header-stat']}>
                                        AUM: <strong>₹{formatNumber(selectedSector.portfolio_aum, 2, 10000000)} Cr</strong>
                                    </div>
                                    <div className={styles['modal-header-stat']}>
                                        Companies: <strong>{selectedSector.companies.length}</strong>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className={styles['modal-header-stat']}>
                                        {getBenchmarkName(modalTab)} Weight: <strong>{formatNumber(activeBmWeight)}%</strong>
                                    </div>
                                    <div className={styles['modal-header-stat']}>
                                        Index Constituents: <strong>{activeBmCompanies.length}</strong>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    <Button iconOnly onClick={onClose} aria-label="Close modal">
                        <Icon name="close" size={16} />
                    </Button>
                </div>

                {/* Tabs */}
                {benchmarkTabs.length > 0 && (
                    <div className={styles['modal-tabs']}>
                        <TabBar
                            ariaLabel="Holdings source"
                            value={modalTab}
                            onChange={setModalTab}
                            tabs={[
                                { value: PORTFOLIO_TAB, label: `Portfolio (${selectedSector.companies.length})` },
                                ...benchmarkTabs.map(([bm, companies]) => ({
                                    value: bm,
                                    label: `${getBenchmarkName(bm)} (${companies.length})`,
                                })),
                            ]}
                        />
                    </div>
                )}

                <div className={styles['modal-body']}>
                    {/* Portfolio Companies Tab */}
                    {modalTab === PORTFOLIO_TAB && (
                        selectedSector.companies.length > 0 ? (
                            <div className={styles['modal-table-wrapper']}>
                                <table className={`${shared.table} ${styles['modal-table']}`}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>Rank</th>
                                            <th>Company Name</th>
                                            <th>Symbol</th>
                                            <th className={styles['th-number']}>Weight</th>
                                            <th className={styles['th-number']}>AUM</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {renderPortfolioRows(selectedSector.companies)}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                                <p style={{ color: 'var(--text-dim)', fontWeight: 500, margin: 0 }}>No portfolio holdings in this sector.</p>
                            </div>
                        )
                    )}

                    {/* Benchmark Companies Tab */}
                    {modalTab !== PORTFOLIO_TAB && (
                        activeBmCompanies.length > 0 ? (
                            <div className={styles['modal-table-wrapper']}>
                                <table className={`${shared.table} ${styles['modal-table']}`}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>Rank</th>
                                            <th>Company Name</th>
                                            <th>Symbol</th>
                                            <th className={styles['th-number']}>Weight</th>
                                            <th className={styles['th-number']}>FF Mkt Cap (Cr)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {renderBenchmarkRows(activeBmCompanies)}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                                <p style={{ color: 'var(--text-dim)', fontWeight: 500, margin: 0 }}>No index constituents in this sector for {getBenchmarkName(modalTab)}.</p>
                            </div>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

const SectorWeightComparison: React.FC<SectorWeightComparisonProps> = ({ requestParams, benchmarkOptions, refreshKey, loadingFilters }) => {
    const [sectorData, setSectorData] = useState<SectorWeightDetail[]>([]);
    const [sectorMeta, setSectorMeta] = useState<SectorWeightResponse['meta'] | null>(null);
    // Starts true: the first fetch always runs once the filters are ready.
    const [sectorLoading, setSectorLoading] = useState<boolean>(true);
    const [sectorError, setSectorError] = useState<string | null>(null);
    const [isAccessRestricted, setIsAccessRestricted] = useState(false);
    const [selectedSector, setSelectedSector] = useState<SectorWeightDetail | null>(null);
    const [modalTab, setModalTab] = useState<string>(PORTFOLIO_TAB);
    const [alertsExpanded, setAlertsExpanded] = useState<{ [key: string]: boolean }>({});
    
    // Sorting and comparison states
    const [heatmapSortBy, setHeatmapSortBy] = useState<string>('Portfolio');
    const [heatmapSortOrder, setHeatmapSortOrder] = useState<'asc' | 'desc'>('desc');
    const [comparisonBenchmark, setComparisonBenchmark] = useState<string>('');

    // Table sorting states
    const [tableSortBy, setTableSortBy] = useState<string>('portfolio_weight');
    const [tableSortOrder, setTableSortOrder] = useState<'asc' | 'desc'>('desc');
    const [varianceSortBy, setVarianceSortBy] = useState<string>('portfolio_weight');
    const [varianceSortOrder, setVarianceSortOrder] = useState<'asc' | 'desc'>('desc');

    const getBenchmarkName = (symbol: string) => {
        return benchmarkOptions.find(opt => opt.value === symbol)?.label || symbol;
    };

    useEffect(() => {
        if (loadingFilters) return;

        const fetchSectorHoldings = async () => {
            setSectorLoading(true);
            setSectorError(null);
            setIsAccessRestricted(false);
            try {
                const response = await sectorWeightAPI.getSectorWeightComparison(requestParams);
                if (!response || !response.data) {
                    setSectorData([]);
                    setSectorMeta(null);
                    return;
                }
                if (response.meta?.access_restricted) {
                    setIsAccessRestricted(true);
                    setSectorData([]);
                    setSectorMeta(null);
                    return;
                }
                setSectorData(response.data);
                setSectorMeta(response.meta || null);
                if (response.meta?.benchmarks?.length > 0 && !comparisonBenchmark) {
                    setComparisonBenchmark(response.meta.benchmarks[0]);
                }
            } catch (err: any) {
                const errorMessage = err?.response?.data?.message || 'Failed to load sector weight comparison data';
                setSectorError(errorMessage);
            } finally {
                setSectorLoading(false);
            }
        };

        fetchSectorHoldings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestParams, loadingFilters, refreshKey]);

    // Filtered data for charts (show sectors present in portfolio OR any benchmark)
    const filteredSectorData = useMemo(() => {
        return sectorData.filter(item =>
            item.portfolio_weight >= 1 ||
            item.benchmarks.some(bm => bm.weight >= 1)
        );
    }, [sectorData]);

    const activeBenchmarks = useMemo(() => {
        return sectorMeta?.benchmarks || [];
    }, [sectorMeta]);

    // Donut Chart Data - Optimized for modern feel
    const compositionData = useMemo(() => {
        // Only show sectors with weight
        const sorted = [...sectorData]
            .filter(s => s.portfolio_weight > 0)
            .sort((a, b) => b.portfolio_weight - a.portfolio_weight);

        if (sorted.length === 0) return null;

        const major = sorted.filter(s => s.portfolio_weight >= 1);
        const others = sorted.filter(s => s.portfolio_weight < 1);
        const othersSum = others.reduce((sum, s) => sum + s.portfolio_weight, 0);

        const labels: string[] = major.map(s => s.sector_name);
        const values: number[] = major.map(s => s.portfolio_weight);
        const colors: string[] = major.map((_, idx) => sectorColor(idx));

        if (others.length > 0) {
            labels.push('Others');
            values.push(othersSum);
            colors.push(CHART_COLORS.others);
        }

        return { labels, values, colors, items: major, othersCount: others.length, othersSum };
    }, [sectorData]);



    // Heatmap Data - All sectors vs all benchmarks
    const heatmapData = useMemo(() => {
        const sortedSectors = [...filteredSectorData].sort((a, b) => {
            let aVal, bVal;
            if (heatmapSortBy === 'Portfolio') {
                aVal = a.portfolio_weight;
                bVal = b.portfolio_weight;
            } else {
                aVal = a.benchmarks.find(bm => bm.benchmark_name === heatmapSortBy)?.weight || 0;
                bVal = b.benchmarks.find(bm => bm.benchmark_name === heatmapSortBy)?.weight || 0;
            }
            return heatmapSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

        const yLabels = sortedSectors.map(s => s.sector_name);
        const xLabels = ['Portfolio', ...activeBenchmarks];

        const zValues = sortedSectors.map(sector => {
            const row = [sector.portfolio_weight];
            activeBenchmarks.forEach(bm => {
                const bmData = sector.benchmarks.find(b => b.benchmark_name === bm);
                row.push(bmData ? bmData.weight : 0);
            });
            return row;
        });

        const textValues = sortedSectors.map(sector => {
            const row = [`${sector.portfolio_weight.toFixed(2)}%`];
            activeBenchmarks.forEach(bm => {
                const bmData = sector.benchmarks.find(b => b.benchmark_name === bm);
                row.push(bmData ? `${bmData.weight.toFixed(2)}%` : '0.00%');
            });
            return row;
        });

        return { xLabels, yLabels, zValues, textValues };
    }, [filteredSectorData, activeBenchmarks, heatmapSortBy, heatmapSortOrder]);

    // Table data
    const sortedTableData = useMemo(() => {
        return [...sectorData].sort((a, b) => {
            let aVal, bVal;
            if (tableSortBy === 'sector_name') {
                aVal = a.sector_name;
                bVal = b.sector_name;
                return tableSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            } else if (tableSortBy.startsWith('bm_')) {
                const bmSymbol = tableSortBy.replace('bm_', '');
                aVal = a.benchmarks.find(b => b.benchmark_name === bmSymbol)?.weight || 0;
                bVal = b.benchmarks.find(b => b.benchmark_name === bmSymbol)?.weight || 0;
            } else {
                aVal = (a as any)[tableSortBy] || 0;
                bVal = (b as any)[tableSortBy] || 0;
            }
            return tableSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [sectorData, tableSortBy, tableSortOrder]);

    const handleDownloadCSV = () => {
        const csvQuote = (s: string) => `"${s.replace(/"/g, '""')}"`;

        const headers = [
            csvQuote('Index'),
            csvQuote('Sector Name'),
            csvQuote('Portfolio Weight (%)'),
        ];
        activeBenchmarks.forEach(bm => {
            headers.push(csvQuote(`${getBenchmarkName(bm)} Weight (%)`));
        });

        const rows = sortedTableData.map((item, idx) => {
            const row: string[] = [
                String(idx + 1),
                csvQuote(item.sector_name),
                item.portfolio_weight.toFixed(2),
            ];
            activeBenchmarks.forEach(bm => {
                const bmWeight = item.benchmarks.find(b => b.benchmark_name === bm)?.weight || 0;
                row.push(bmWeight.toFixed(2));
            });
            return row.join(',');
        });

        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'sector_weight_comparison.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const sortedVarianceData = useMemo(() => {
        return [...sectorData].sort((a, b) => {
            let aVal, bVal;
            if (varianceSortBy === 'sector_name') {
                aVal = a.sector_name;
                bVal = b.sector_name;
                return varianceSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            } else if (varianceSortBy === 'bm_weight') {
                aVal = a.benchmarks.find(b => b.benchmark_name === comparisonBenchmark)?.weight || 0;
                bVal = b.benchmarks.find(b => b.benchmark_name === comparisonBenchmark)?.weight || 0;
            } else if (varianceSortBy === 'difference') {
                const aBmWeight = a.benchmarks.find(b => b.benchmark_name === comparisonBenchmark)?.weight || 0;
                const bBmWeight = b.benchmarks.find(b => b.benchmark_name === comparisonBenchmark)?.weight || 0;
                aVal = a.portfolio_weight - aBmWeight;
                bVal = b.portfolio_weight - bBmWeight;
            } else {
                aVal = (a as any)[varianceSortBy] || 0;
                bVal = (b as any)[varianceSortBy] || 0;
            }
            return varianceSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [sectorData, comparisonBenchmark, varianceSortBy, varianceSortOrder]);

    // Count invested sectors (only sectors with portfolio_weight > 0)
    const investedSectorsCount = useMemo(() => {
        return sectorData.filter(item => item.portfolio_weight > 0).length;
    }, [sectorData]);

    // Calculate totals
    const totals = useMemo(() => {
        const totalPortfolioWeight = sectorData.reduce((sum, item) => sum + item.portfolio_weight, 0);
        const totalPortfolioAUM = sectorData.reduce((sum, item) => sum + item.portfolio_aum, 0);

        const benchmarkTotals: { [key: string]: number } = {};
        activeBenchmarks.forEach(bm => {
            benchmarkTotals[bm] = sectorData.reduce((sum, item) => {
                const bmWeight = item.benchmarks.find(b => b.benchmark_name === bm)?.weight || 0;
                return sum + bmWeight;
            }, 0);
        });

        return {
            portfolioWeight: totalPortfolioWeight,
            portfolioAUM: totalPortfolioAUM,
            benchmarks: benchmarkTotals
        };
    }, [sectorData, activeBenchmarks]);

    const alerts: SectorWeightAlerts | undefined = sectorMeta?.alerts;
    const missingMcapEntries = alerts?.missing_free_float_market_cap && typeof alerts.missing_free_float_market_cap === 'object'
        ? Object.entries(alerts.missing_free_float_market_cap) : [];
    const unclassifiedList = Array.isArray(alerts?.unclassified_companies) ? alerts.unclassified_companies : [];
    const hasAlerts = alerts && (
        missingMcapEntries.some(
            ([benchmark, companies]) => Array.isArray(companies) && companies.length > 0 && benchmarkOptions.some(opt => opt.value === benchmark)
        ) ||
        unclassifiedList.length > 0
    );

    const toggleAlertExpand = (key: string) => {
        setAlertsExpanded(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleTableSort = (field: string) => {
        if (tableSortBy === field) {
            setTableSortOrder(tableSortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setTableSortBy(field);
            setTableSortOrder('desc');
        }
    };

    const handleVarianceSort = (field: string) => {
        if (varianceSortBy === field) {
            setVarianceSortOrder(varianceSortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setVarianceSortBy(field);
            setVarianceSortOrder('desc');
        }
    };

    const SortIndicator = ({ field, sortBy, sortOrder }: { field: string, sortBy: string, sortOrder: 'asc' | 'desc' }) => {
        if (sortBy !== field) return <span style={{ opacity: 0.3, marginLeft: '4px' }}>↕</span>;
        return <span style={{ marginLeft: '4px', color: 'var(--accent)' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
    };

    const handleSectorClick = (sectorName: string) => {
        if (sectorName === 'Others') return;
        const sector = sectorData.find(s => s.sector_name === sectorName);
        if (sector) {
            setSelectedSector(sector);
            setModalTab(PORTFOLIO_TAB);
        }
    };

    const onChartClick = (event: any) => {
        if (event.points && event.points[0]) {
            const sectorName = event.points[0].label || event.points[0].y;
            handleSectorClick(sectorName);
        }
    };

    // While the parent is still resolving filter defaults the fetch hasn't started yet —
    // show the loader rather than "No Holdings Found".
    if (sectorLoading || loadingFilters) {
        return (
            <Surface>
                <Placeholder kind="loading" message="Analyzing sector weights..." />
            </Surface>
        );
    }

    if (sectorError) {
        return <Note tone="error">{sectorError}</Note>;
    }

    if (isAccessRestricted) {
        return (
            <Note tone="warning">
                Data for this date requires complete access. Contact your administrator.
            </Note>
        );
    }

    if (sectorData.length === 0) {
        return (
            <Surface>
                <Placeholder
                    message="No Holdings Found"
                    hint="No holdings data available for the selected filters. Please try adjusting the investment strategies, schemes, or benchmarks."
                />
            </Surface>
        );
    }

    return (
        <>
            {/* Summary Stats */}
            <StatGrid
                items={[
                    { label: 'Invested Sectors', value: investedSectorsCount },
                    { label: 'Portfolio AUM', value: `₹${formatNumber(totals.portfolioAUM, 0, 10000000)} Cr` },
                    {
                        label: 'Top Sector',
                        value: sortedTableData[0]?.sector_name || 'N/A',
                        hint: sortedTableData[0] && `${formatNumber(sortedTableData[0]?.portfolio_weight || 0)}% allocation`,
                    },
                    { label: 'Benchmarks', value: activeBenchmarks.length },
                    ...(sectorMeta?.holding_date
                        ? [{ label: 'Data As Of', value: formatDate(sectorMeta.holding_date) }]
                        : []),
                ]}
            />

            {/* Data Quality Alerts */}
            {hasAlerts && (
                <Note tone="warning">
                    <div className={styles['alerts-banner-items']}>
                        {missingMcapEntries
                            .filter(([benchmark, companies]) =>
                                Array.isArray(companies) &&
                                companies.length > 0 &&
                                benchmarkOptions.some(opt => opt.value === benchmark)
                            )
                            .map(([benchmark, companies]) => (
                                <div key={benchmark}>
                                    <span>{companies.length} companies in <strong>{benchmark}</strong> have missing free float market cap data</span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleAlertExpand(`mcap-${benchmark}`)}
                                >
                                    {alertsExpanded[`mcap-${benchmark}`] ? 'Hide' : 'Show'} details
                                </Button>
                                {alertsExpanded[`mcap-${benchmark}`] && (
                                    <div className={styles['alert-detail-list']}>
                                        {companies.map((c) => (
                                            <span key={c.symbol} className={styles['alert-detail-chip']}>{c.symbol}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {unclassifiedList.length > 0 && (
                            <div>
                                <span>{unclassifiedList.length} portfolio companies have no sector assigned</span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleAlertExpand('unclassified')}
                                >
                                    {alertsExpanded['unclassified'] ? 'Hide' : 'Show'} details
                                </Button>
                                {alertsExpanded['unclassified'] && (
                                    <div className={styles['alert-detail-list']}>
                                        {unclassifiedList.map((c) => (
                                            <span key={c.symbol} className={styles['alert-detail-chip']}>{c.symbol}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Note>
            )}

            {/* Charts Section */}
            <div className={styles['charts-section']}>
                {/* Sunburst Chart */}
                <Surface
                    className={styles['chart-span']}
                    title="Portfolio Composition"
                    meta="Sectors at 1% or more shown separately; the rest grouped as Others"
                    padded
                >
                    <div className={styles['composition-layout']}>
                        <div className={styles['composition-chart']}>
                            <Plot
                                data={[
                                    {
                                        values: compositionData?.values || [],
                                        labels: compositionData?.labels || [],
                                        type: 'pie',
                                        hole: 0.65,
                                        marker: {
                                            // The same list the legend reads, so a slice and its swatch always match.
                                            colors: compositionData?.colors || [],
                                            line: { color: chartInk.surface, width: 2 }
                                        },
                                        textposition: 'outside',
                                        automargin: true,
                                        hoverinfo: 'label+percent',
                                        pull: 0.01,
                                    } as any
                                ]}
                                onClick={onChartClick}
                                layout={{
                                    showlegend: false,
                                    margin: { t: 30, l: 30, r: 30, b: 30 },
                                    paper_bgcolor: 'transparent',
                                    height: 420,
                                }}
                                config={{ displayModeBar: false }}
                                style={{ width: '100%', height: '100%' }}
                            />
                            <div className={styles['donut-center']}>
                                <div className={styles['donut-value']}>{compositionData?.items.length || 0}</div>
                                <div className={styles['donut-label']}>Sectors</div>
                            </div>
                        </div>
                        <div className={styles['composition-legend']}>
                            <div className={styles['legend-header']}>Portfolio Concentration</div>
                            <div className={styles['legend-list']}>
                                {compositionData?.items.slice(0, 8).map((item, idx) => (
                                    <div 
                                        key={item.sector_name} 
                                        className={`${styles['legend-item']} ${styles['clickable']}`}
                                        onClick={() => handleSectorClick(item.sector_name)}
                                    >
                                        <div className={styles['legend-swatch']} style={{ backgroundColor: compositionData?.colors[idx] }}></div>
                                        <div className={styles['legend-name']}>{item.sector_name}</div>
                                        <div className={styles['legend-weight']}>{formatNumber(item.portfolio_weight)}%</div>
                                    </div>
                                ))}
                                {compositionData && compositionData.othersSum > 0 && (
                                    <div className={styles['legend-item']}>
                                        <div className={styles['legend-swatch']} style={{ backgroundColor: CHART_COLORS.others }}></div>
                                        <div className={styles['legend-name']}>Others ({compositionData.othersCount})</div>
                                        <div className={styles['legend-weight']}>{formatNumber(compositionData.othersSum)}%</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Surface>

                {/* Heatmap */}
                <Surface
                    className={styles['chart-span']}
                    title="Sector Weight Heatmap"
                    meta="Every portfolio against every benchmark"
                    actions={
                            <div className={styles['sort-controls']} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>Sort By:</label>
                                <select 
                                    className={styles['select-control']}
                                    value={heatmapSortBy} 
                                    onChange={(e) => setHeatmapSortBy(e.target.value)}
                                >
                                    <option value="Portfolio">Portfolio</option>
                                    {activeBenchmarks.map(bm => (
                                        <option key={bm} value={bm}>{getBenchmarkName(bm)}</option>
                                    ))}
                                </select>
                                <select 
                                    className={styles['select-control']}
                                    value={heatmapSortOrder} 
                                    onChange={(e) => setHeatmapSortOrder(e.target.value as 'asc' | 'desc')}
                                >
                                    <option value="desc">Descending</option>
                                    <option value="asc">Ascending</option>
                                </select>
                            </div>
                    }
                    padded
                >
                    <div className={styles['chart-container']}>
                        <Plot
                            data={[
                                {
                                    type: 'heatmap',
                                    z: heatmapData.zValues,
                                    x: heatmapData.xLabels,
                                    y: heatmapData.yLabels,
                                    text: heatmapData.textValues,
                                    texttemplate: '%{text}',
                                    textfont: { size: 10, family: plotlyLayout.font.family },
                                    colorscale: HEATMAP_SCALE,
                                    colorbar: {
                                        title: {
                                            text: 'Weight (%)',
                                            side: 'right',
                                            font: { size: 10 }
                                        },
                                        thickness: 12,
                                        len: 0.8,
                                        outlinecolor: 'transparent'
                                    },
                                    hovertemplate: '<b>%{y}</b><br>%{x}: <b>%{z:.2f}%</b><extra></extra>',
                                } as any
                            ]}
                            onClick={onChartClick}
                            layout={{
                                margin: { t: 20, l: 180, r: 80, b: 80 },
                                paper_bgcolor: 'transparent',
                                plot_bgcolor: 'transparent',
                                font: { family: plotlyLayout.font.family, size: 11, color: chartInk.label },
                                xaxis: {
                                    side: 'bottom',
                                    tickangle: -45,
                                },
                                yaxis: {
                                    tickfont: { size: 10 },
                                    automargin: true,
                                    autorange: 'reversed',
                                },
                                height: Math.max(500, heatmapData.yLabels.length * 25),
                            }}
                            config={{
                                displayModeBar: true,
                                modeBarButtonsToRemove: ['zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d', 'hoverClosestCartesian', 'hoverCompareCartesian'],
                                displaylogo: false,
                                toImageButtonOptions: {
                                    format: 'png',
                                    filename: `sector_heatmap_${new Date().toISOString().slice(0, 10)}`,
                                    height: 1000,
                                    width: 1200,
                                    scale: 2
                                }
                            }}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                </Surface>
            </div>

            {/* Benchmark Difference Comparison Section */}
            <Surface
                title="Portfolio vs Benchmark Variance"
                meta="Weight difference against the selected benchmark"
                actions={
                        <div className={styles['comparison-selector']}>
                            <label style={{ marginRight: '10px', fontSize: '0.875rem', fontWeight: 500 }}>Compare with:</label>
                            <select 
                                className={styles['select-control']}
                                value={comparisonBenchmark} 
                                onChange={(e) => setComparisonBenchmark(e.target.value)}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--border)' }}
                            >
                                {activeBenchmarks.map(bm => (
                                    <option key={bm} value={bm}>{getBenchmarkName(bm)}</option>
                                ))}
                            </select>
                        </div>
                }
            >
                <div className={shared['table-scroll']}>
                    <table className={`${shared.table} ${styles['data-table']}`}>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Index</th>
                                <th className={styles['clickable']} onClick={() => handleVarianceSort('sector_name')}>
                                    Sector Name <SortIndicator field="sector_name" sortBy={varianceSortBy} sortOrder={varianceSortOrder} />
                                </th>
                                <th className={`${styles['th-number']} ${styles['clickable']}`} onClick={() => handleVarianceSort('portfolio_weight')}>
                                    Portfolio Weight <SortIndicator field="portfolio_weight" sortBy={varianceSortBy} sortOrder={varianceSortOrder} />
                                </th>
                                <th className={`${styles['th-number']} ${styles['clickable']}`} onClick={() => handleVarianceSort('bm_weight')}>
                                    {getBenchmarkName(comparisonBenchmark) || 'Benchmark'} Weight <SortIndicator field="bm_weight" sortBy={varianceSortBy} sortOrder={varianceSortOrder} />
                                </th>
                                <th className={`${styles['th-number']} ${styles['clickable']}`} onClick={() => handleVarianceSort('difference')}>
                                    Difference <SortIndicator field="difference" sortBy={varianceSortBy} sortOrder={varianceSortOrder} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedVarianceData.map((item, idx) => {
                                const bmWeight = item.benchmarks.find(b => b.benchmark_name === comparisonBenchmark)?.weight || 0;
                                const diff = item.portfolio_weight - bmWeight;
                                return (
                                    <tr key={item.sector_name} className={`${idx % 2 === 0 ? styles['row-even'] : ''} ${item.is_unclassified ? styles['unclassified-row'] : ''}`}>
                                        <td>{idx + 1}</td>
                                        <td
                                            className={`${styles['td-sector']} ${styles['clickable']}`}
                                            onClick={() => handleSectorClick(item.sector_name)}
                                        >
                                            {item.sector_name}
                                            {item.is_unclassified && (
                                                <span className={styles['unclassified-badge']}>
                                                    <WarningGlyph size={12} />
                                                    No Sector
                                                </span>
                                            )}
                                        </td>
                                        <td className={styles['td-number']}>{formatNumber(item.portfolio_weight)}%</td>
                                        <td className={styles['td-number']}>{formatNumber(bmWeight)}%</td>
                                        <td className={`${styles['td-number']} ${signedClass(diff)}`}>
                                            {diff > 0 ? '+' : ''}{formatNumber(diff)}%
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Surface>

            {/* Detailed sector analysis */}
            <Surface
                title="Detailed Sector Analysis"
                meta="Every sector in the portfolio"
                actions={
                    <Button size="sm" onClick={handleDownloadCSV} disabled={sortedTableData.length === 0}>
                        <Icon name="download" size={14} /> Download CSV
                    </Button>
                }
            >
                <div className={shared['table-scroll']}>
                    <table className={`${shared.table} ${styles['data-table']}`}>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Index</th>
                                <th className={styles['clickable']} onClick={() => handleTableSort('sector_name')}>
                                    Sector Name <SortIndicator field="sector_name" sortBy={tableSortBy} sortOrder={tableSortOrder} />
                                </th>
                                <th className={`${styles['th-number']} ${styles['clickable']}`} onClick={() => handleTableSort('portfolio_weight')}>
                                    Portfolio Weight <SortIndicator field="portfolio_weight" sortBy={tableSortBy} sortOrder={tableSortOrder} />
                                </th>
                                {activeBenchmarks.map(bm => (
                                    <th key={bm} className={`${styles['th-number']} ${styles['clickable']}`} onClick={() => handleTableSort(`bm_${bm}`)}>
                                        {getBenchmarkName(bm)} Weight <SortIndicator field={`bm_${bm}`} sortBy={tableSortBy} sortOrder={tableSortOrder} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTableData.map((item, idx) => (
                                <tr key={item.sector_name} className={`${idx % 2 === 0 ? styles['row-even'] : ''} ${item.is_unclassified ? styles['unclassified-row'] : ''}`}>
                                    <td>{idx + 1}</td>
                                    <td
                                        className={`${styles['td-sector']} ${styles['clickable']}`}
                                        onClick={() => handleSectorClick(item.sector_name)}
                                    >
                                        {item.sector_name}
                                        {item.is_unclassified && (
                                            <span className={styles['unclassified-badge']}>
                                                <WarningGlyph size={12} />
                                                No Sector
                                            </span>
                                        )}
                                    </td>
                                    <td className={styles['td-number']}>
                                        {formatNumber(item.portfolio_weight)}%
                                    </td>
                                    {activeBenchmarks.map(bm => {
                                        const bmWeight = item.benchmarks.find(b => b.benchmark_name === bm)?.weight || 0;
                                        return (
                                            <td key={bm} className={styles['td-number']}>
                                                {formatNumber(bmWeight)}%
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Surface>

            {/* Company List Modal */}
            {selectedSector && (
                <SectorDetailModal
                    selectedSector={selectedSector}
                    modalTab={modalTab}
                    setModalTab={setModalTab}
                    onClose={() => setSelectedSector(null)}
                    benchmarkOptions={benchmarkOptions}
                />
            )}
        </>
    );
};

export default SectorWeightComparison;
